/**
 * Opens the Finish sheet from anywhere (Today's rows, Happening now, pending close-outs, the crew view,
 * Needs your attention for a returned close-out), and the two small presses that sit beside it: Mark
 * completed (staff units and facility spaces — no completion record) and Send update (after an edit).
 * Finishing is for the administrator, co-administrators, the scheduler and the crew — never a viewer.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Role } from '@shared/roles';
import type { CloseoutMarker } from '@shared/finish';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { useAnnounce } from '@/components/Announcer';
import { useToast } from '@/components/ui/use-toast';
import { Sheet } from '@/components/ui/sheet';
import { FinishSheet, type FinishMode, type FinishTarget } from './FinishSheet';
import { ConfirmCompleted } from '@/features/press/Press';
import { pressName } from '@shared/press';
import { formatClock } from '@shared/dayplan';
import { serviceMinutesNow } from '@shared/dates';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Icon } from '@/components/Icons';
import { WhenControl, type WhenValue } from '@/features/daybend/WhenControl';

export const FINISH_ROLES: readonly Role[] = ['admin', 'coadmin', 'scheduler', 'crew'];

interface Open {
  target: FinishTarget;
  mode: FinishMode;
  completionId?: string | null;
  returnNotes?: string | null;
  n: number;
}

interface FinishCtx {
  canFinish: boolean;
  openFinish: (target: FinishTarget, opener?: HTMLElement | null, opts?: { mode?: FinishMode; completionId?: string | null; returnNotes?: string | null }) => void;
  markCompleted: (target: FinishTarget) => Promise<void>;
  /** 1.6.8: Start on a staff unit or an office — the time and who, after one line of confirmation. */
  startStaff: (target: FinishTarget) => Promise<void>;
  sendUpdate: (marker: CloseoutMarker, unit: string) => Promise<void>;
}

/**
 * 1.7.8 (D-1.7.8-1): Start on a staff unit or an office asks the same one line, with When — Now, or an earlier time
 * recorded later (a time still to come is not offered). The test ids are the 1.6.8 confirm's.
 */
function ConfirmStart({ target, busy, error, onYes, onNo }: { target: FinishTarget; busy: boolean; error: string | null; onYes: (at: number | null) => void; onNo: () => void }) {
  const { t } = useTranslation();
  const [when, setWhen] = useState<WhenValue>({ mode: 'now' });
  const booked = typeof target.chosenStart === 'number' && target.chosenStart < serviceMinutesNow() ? target.chosenStart : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onNo()}>
      <DialogContent data-testid="confirm-start" className="confirm-start-178">
        <DialogTitle>{t('card168.confirmStartTitle', { unit: target.unit })}</DialogTitle>
        <DialogDescription>{when.mode === 'time' ? t('book178.confirmStartAt', { unit: target.unit, time: formatClock(when.minutes) }) : t('card168.confirmStartLine', { unit: target.unit })}</DialogDescription>
        <div className="start-when" data-testid="start-when-block">
          <WhenControl
            id="start-when"
            day={target.serviceDateKey}
            value={when}
            onChange={setWhen}
            allowFuture={false}
            suggest={booked !== null ? { minutes: booked, label: t('book178.bookedFor', { time: formatClock(booked) }) } : null}
          />
        </div>
        {error ? (
          <div className="warn take" role="alert" data-testid="confirm-start-error">
            <Icon name="alert" />
            <div>{error}</div>
          </div>
        ) : null}
        <div className="chips">
          <Button type="button" variant="secondary" onClick={onNo} disabled={busy} data-testid="confirm-start-no">
            {t('press165.notYet')}
          </Button>
          <Button type="button" onClick={() => onYes(when.mode === 'time' ? when.minutes : null)} disabled={busy} data-testid="confirm-start-yes">
            {t('card168.yesStart')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Ctx = createContext<FinishCtx>({ canFinish: false, openFinish: () => undefined, markCompleted: async () => undefined, startStaff: async () => undefined, sendUpdate: async () => undefined });

export function FinishProvider({ role, children }: { role: Role | null; children: ReactNode }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  const canFinish = !!role && FINISH_ROLES.includes(role);
  const [open, setOpen] = useState<Open | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const seq = useRef(0);

  const refresh = useCallback(() => {
    for (const k of ['today', 'attention', 'appointments', 'tenant-history', 'completions', 'closeout']) void qc.invalidateQueries({ queryKey: [k] });
  }, [qc]);

  const openFinish = useCallback<FinishCtx['openFinish']>(
    (target, el, opts) => {
      if (!canFinish) return;
      opener.current = el ?? (document.activeElement as HTMLElement | null);
      setOpen({ target, mode: opts?.mode ?? 'record', completionId: opts?.completionId, returnNotes: opts?.returnNotes, n: ++seq.current });
    },
    [canFinish],
  );

  const say = useCallback(
    (m: string, assertive = false) => {
      toast(m);
      announce(m, assertive ? 'assertive' : 'polite');
    },
    [announce, toast],
  );

  // 1.6.5 (D-1.6.5-1): Mark completed asks first — one line, Yes or Not yet; nothing is sent by the first tap
  const [confirming, setConfirming] = useState<{ target: FinishTarget; what: 'start' | 'end'; done: () => void } | null>(null);
  const [marking, setMarking] = useState(false);
  /** 1.7.8: a refusal the confirm keeps open for (a time still to come). */
  const [startError, setStartError] = useState<string | null>(null);
  const markCompleted = useCallback<FinishCtx['markCompleted']>(
    (target) =>
      new Promise<void>((done) => {
        if (!canFinish) return done();
        opener.current = document.activeElement as HTMLElement | null;
        setConfirming({ target, what: 'end', done });
      }),
    [canFinish],
  );
  const startStaff = useCallback<FinishCtx['startStaff']>(
    (target) =>
      new Promise<void>((done) => {
        if (!canFinish) return done();
        opener.current = document.activeElement as HTMLElement | null;
        setConfirming({ target, what: 'start', done });
      }),
    [canFinish],
  );
  const closeConfirm = useCallback(() => {
    setConfirming((c) => {
      c?.done();
      return null;
    });
    setStartError(null);
    const el = opener.current;
    if (el?.isConnected) window.requestAnimationFrame(() => el.focus());
  }, []);
  const sendMark = useCallback(async (at: number | null = null) => {
    const target = confirming?.target;
    if (!target) return;
    const start = confirming.what === 'start';
    setMarking(true);
    setStartError(null);
    let stay = false;
    try {
      // 1.7.8: `at` (the minute the crew began) travels only when a time other than Now was chosen
      if (start) await apiFetch('/api/housekeeping/staff-start', { method: 'POST', body: JSON.stringify({ appointmentId: target.appointmentId, ...(at !== null ? { at } : {}) }) });
      else await apiFetch(`/api/appointments/${encodeURIComponent(target.appointmentId)}/mark-completed`, { method: 'POST' });
      say(t(start ? 'card168.started' : 'card168.ended', { unit: target.unit }));
    } catch (e) {
      const body = e instanceof ApiRequestError ? (e.body as { error?: string; message?: string; by?: string } | undefined) : undefined;
      // another phone was first: say who, and show the card as it now is
      if (start && body?.error === 'future') {
        // a time still to come: the confirm stays, with the server's words — another time can be chosen
        stay = true;
        const m = body.message ?? t('book178.futureRefused');
        setStartError(m);
        announce(m, 'assertive');
      } else if (body?.error === 'already') say(body.by ? t(start ? 'card168.startedNow' : 'card168.endedNow', { name: pressName(body.by) }) : t(start ? 'card168.someoneStarted' : 'card168.someoneEnded'), true);
      else say(t(start ? 'card168.startFailed' : 'today.markFailed', { message: body?.message ?? (e as Error).message }), true);
    } finally {
      setMarking(false);
      if (!stay) {
        refresh();
        closeConfirm();
      }
    }
  }, [announce, closeConfirm, confirming, refresh, say, t]);

  const sendUpdate = useCallback<FinishCtx['sendUpdate']>(
    async (marker) => {
      try {
        await apiFetch(`/api/closeouts/${encodeURIComponent(marker.transactionId)}/send-update`, { method: 'POST' });
        say(t('today.updateSent'));
        refresh();
      } catch (e) {
        say(e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message, true);
      }
    },
    [refresh, say, t],
  );

  const value = useMemo(() => ({ canFinish, openFinish, markCompleted, startStaff, sendUpdate }), [canFinish, openFinish, markCompleted, startStaff, sendUpdate]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {confirming?.what === 'start' ? (
        <ConfirmStart target={confirming.target} busy={marking} error={startError} onYes={(at) => void sendMark(at)} onNo={closeConfirm} />
      ) : confirming ? (
        <ConfirmCompleted what={confirming.what} unit={confirming.target.unit} busy={marking} onYes={() => void sendMark()} onNo={closeConfirm} />
      ) : null}
      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        {open ? (
          <FinishSheet
            key={open.n}
            target={open.target}
            mode={open.mode}
            completionId={open.completionId}
            returnNotes={open.returnNotes}
            returnFocusTo={opener}
            onDone={() => {
              setOpen(null);
              refresh();
            }}
          />
        ) : null}
      </Sheet>
    </Ctx.Provider>
  );
}

export function useFinish(): FinishCtx {
  return useContext(Ctx);
}
